import React from "react";
import Layer from "../../Layer";
import MUIGreyColor from "@mui/material/colors/grey";
import * as MUIColors from "@mui/material/colors";
import CardReportSettings from "../../Component/Card/Report/CardReportSettings";
import CardReportOrderTimelineAuditChart from "../../Component/Card/Report/OrderTimelineAudit/CardReportOrderTimelineAuditChart";
import CardReportOrderTimelineAuditResults from "../../Component/Card/Report/OrderTimelineAudit/CardReportOrderTimelineAuditResults";
import OrderStatusReason from "../../Utility/Crud/OrderStatusReason";
import Location from "../../Utility/Crud/Location";
import User from "../../Utility/Crud/User";
import Setting from "../../Utility/Setting";
import InsuranceSubType from "../../Utility/Crud/InsuranceSubType";
import PageHeader from "../../Component/PageHeader";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import api from "../../Seating/Security/api";
import MainContext from "../../Context/MainContext/MainContext";

export default class OrderTimelineAudit extends Layer {
    static contextType = MainContext; // For legacy class components

    afterComponentDidMount() {
        this.setState({
            grouping: Setting.get(`reports.${this.getDetailKey()}.grouping`) || [],
            sorting: Setting.get(`reports.${this.getDetailKey()}.sorting`) || [],
            filtering: null,
            sankeyChartCollapsed: Setting.get(`${this.getDetailKey()}.sankeyChartCollapsed`),
            otherCategoryCollapsed: Setting.get(`${this.getDetailKey()}.otherCategoryCollapsed`),
            marketingCampaignFilter: Setting.get(`reports.${this.getDetailKey()}.marketingCampaignFilter`) || false,
        }, () => {
            this.setColumnWidths();
        });
    }

    columns = [
        {
            title: "Order #",
            name: "audit_log.row_id",
            type: "number",
            width: 110,
        },
        {
            title: "Order Type",
            name: "audit_log.order_activity.order_type",
            type: "string",
            width: 120,
        },
        {
            title: "Date Routed",
            name: "audit_log.timestamp",
            type: "dateTime",
            width: 130,
        },
        {
            title: "User",
            name: "audit_log.userprofile_id",
            type: "string",
            width: 140,
        },
        {
            title: "Old Reason",
            name: "audit_log.old_value",
            type: "string",
            width: 160,
        },
        {
            title: "New Reason",
            name: "audit_log.new_value",
            type: "string",
            width: 160,
        },
        {
            title: "Pri Ins Type",
            name: "audit_log.order_activity.insurance_sub_type_id",
            type: "string",
            width: 140,
        },
        {
            title: "Sales Location",
            name: "audit_log.order_activity.sales_location_id",
            type: "string",
            width: 150,
        },
        {
            title: "Owner",
            name: "audit_log.order_activity.activity.owner_id",
            type: "string",
            width: 140
        },
        {
            title: "Points",
            name: "audit_log.order_activity.order_activity_products._sum_points",
            type: "points",
            width: 90
        },
    ];

    /**
     * Returns the ordered list of column names to be used
     * in the CSV export. This determines both the column headers
     * and the order in which data is displayed in the export file.
     *
     * @returns {string[]} An array of column header names in the desired export order.
     */
    getCSVExportColumnNames() {
        return {
            "order timeline audit": [
                "Order ID",
                "Order Type",
                "Date",
                "User",
                "Old Reason",
                "New Reason",
                "Insurance Sub Type",
                "Sales Location",
                "Service Location",
                "Owner",
                "Points",
            ]
        };;
    }

    /**
     * Returns a camel-cased string to use as a unique identifier
     * for the subclass, based on its `getTitle()` implementation.
     */
    getDetailKey() {
        const header = this.getTitle();
        if (!header) return "orderTimelineAudit";

        const noSpaces = header.replace(/[^a-zA-Z0-9]+/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');

        const lowercaseFirstLetter = noSpaces.charAt(0).toLowerCase() + noSpaces.slice(1);

        return `reports.${lowercaseFirstLetter}`;
    }


    /**
     * Sets the column widths after component has mounted.
     */
    setColumnWidths() {
        let defaultColumnWidths = [];

        this.columns.forEach((column) => {
            defaultColumnWidths.push({
                columnName: column.name,
                width: column.width
            })
        })

        this.setState({
            defaultColumnWidths: defaultColumnWidths,
        })
    }

    /**
     * Sets new filtering in state and local storage.
     * @param {*} filtering
     */
    handleFilteringChange(filtering) {
        this.setState({
            filtering: filtering,
        });
    }

    handleDelete(data) {
        this.setState({
            filtering: this.state.filtering.filter((item) => item.id !== data.id),
        });
    }

    /**
     * Returns array with unique values for filtering
     * @param {array}
     */
    handleFilteringData(item) {
        const rows = this.state.rows;
        // Remove zero-width spaces from Sankey node ids
        const newReasonName = item?.target?.id.replace(/\u200B/g, "");
        const oldReasonName = item?.source?.id.replace(/\u200B/g, "");

        this.setState((prevState) => {
            // Get all existing filter pairs
            const existingPairs = (prevState.filtering || []).map(filter => `${filter.id}|${filter.oldId}`);

            // Find rows matching both new and old reason strings
            const matchedRows = rows.filter(row =>
                row["audit_log.new_value_id"] !== null &&
                row["audit_log.old_value_id"] !== null &&
                row["audit_log.new_value"].trim().replace(/\u200B/g, "") === newReasonName &&
                row["audit_log.old_value"].trim().replace(/\u200B/g, "") === oldReasonName
            );

            if (matchedRows.length === 0) {
            // No matching rows, do not update filter
                return prevState;
            }

            // Use the last matched row for the filter
            const lastMatch = matchedRows[matchedRows.length - 1];
            const newReasonId = lastMatch["audit_log.new_value_id"];
            const oldReasonId = lastMatch["audit_log.old_value_id"];

            let color;
            // If any relevant value is null, use gray
            const orderStatusReasonObj = lastMatch && this.context.orderStatusReasonsIndexed[lastMatch["audit_log.new_value_id"]];
            const orderStatus = orderStatusReasonObj && this.context.orderStatusesIndexed[orderStatusReasonObj.orderStatus];
            const colorObject = orderStatus && this.context.colorsIndexed[orderStatus.colorId];

            if (
                !lastMatch ||
                !orderStatusReasonObj ||
                !orderStatus ||
                !colorObject
            ) {
                color = MUIGreyColor[500];
            } else {
                color = MUIColors[colorObject.hue][colorObject.shade] ?? MUIGreyColor[500];
            }

            const avatarColor = this.props.isTransparent ? "transparent" : color;
            const iconColor = this.props.isTransparent ? color : "#fff";
            // Prevent adding duplicate filter pairs
            const pairKey = `${newReasonId}|${oldReasonId}`;
            if (!newReasonId || !oldReasonId || existingPairs.includes(pairKey)) {
                return prevState;
            }

            // Add new filter pair to filtering array
            return {
                ...prevState,
                filtering: [
                    ...(prevState.filtering || []),
                    { id: newReasonId, oldId: oldReasonId, oldReasonName: oldReasonName, newReasonName: newReasonName, iconColor: iconColor, avatarColor: avatarColor }
                ]
            };
        });
    }

    /**
     * Sets new grouping in state and local storage.
     */
    handleGroupingChange(grouping) {
        this.setState({
            grouping: grouping,
        })

        Setting.set(`reports.${this.getDetailKey()}.grouping`, grouping);
    }

    /**
     * Sets new sorting in state and local storage.
     */
    handleSortingChange(sorting) {
        this.setState({
            sorting: sorting,
        })

        Setting.set(`reports.${this.getDetailKey()}.sorting`, sorting);
    }
    /**
     * @param {Object} settings - Configuration settings for the metrics
     * request.
     */
    async getOrderTimelineAudit(settings) {
        this.setState({ loading: true })

        let transformedSettings = {
            beginDate: settings.dateRange.beginDate,
            endDate: settings.dateRange.endDate,
            oldOrderStatusReasonIds: settings?.orderStatusReasons?.oldOrderStatusReasonIds,
            newOrderStatusReasonIds: settings?.orderStatusReasons?.newOrderStatusReasonIds,
            marketingCampaignFilter: settings?.marketingCampaignFilter?.marketingCampaignFilter,
            orderActivityTypes: settings?.orderActivityTypes?.orderActivityTypes,
        };
        if (
            settings?.userByLocation?.userprofileIds?.length === 0 &&
            settings?.userByLocation?.locationIds?.length > 0
        ) {
            // If a location was selected but no users, choose all the users at
            // that location.
            transformedSettings.users = User.read(
                this.context.usersIndexed,
                [
                    {
                        key: "active",
                        value: true
                    },
                    {
                        key: "id",
                        value: Object.values(this.context.userprofileLocationsIndexed)
                            .filter((userprofileLocation) => {
                                return settings.userByLocation.locationIds.includes(userprofileLocation.locationsId)
                            })
                            .map((userProfileLocation) => {
                                return userProfileLocation.userprofileId
                            })
                    }
                ]
            ).map(userprofile => userprofile.id);
        } else if (settings?.userByLocation?.userprofileIds?.length > 0) {
            transformedSettings.userIds = settings.userByLocation.userprofileIds;
        } else {
            transformedSettings.userIds = [];
        }

        return await api.send("POST", "/reports/orderTimelineAudit", transformedSettings)
            .then((orderActivityLogs) => {
                const transformedRows = orderActivityLogs.map((entry) => {

                    // group of constants to get display names without needing to use the table.
                    const user = User.getDisplayName(this.context.usersIndexed[entry["audit_log.userprofile_id"]])
                        ? User.getDisplayName(this.context.usersIndexed[entry["audit_log.userprofile_id"]])
                        : "";
                    const owner = User.getDisplayName(this.context.usersIndexed[entry["audit_log.order_activity.activity.owner_id"]])
                        ? User.getDisplayName(this.context.usersIndexed[entry["audit_log.order_activity.activity.owner_id"]])
                        : "";
                    const oldReasonName =
                        OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.old_value"]])
                            ? OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.old_value"]])
                            : "No Reason";
                    const newReasonName =
                        OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.new_value"]])
                            ? OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.new_value"]])
                            : "No Reason";
                    const insuranceSubTypeName =
                        InsuranceSubType.getDisplayName(this.context.insuranceSubTypesIndexed[entry["audit_log.order_activity.insurance_sub_type_id"]])
                            ? InsuranceSubType.getDisplayName(this.context.insuranceSubTypesIndexed[entry["audit_log.order_activity.insurance_sub_type_id"]])
                            : "";
                    const salesLocation = Location.getDisplayName(this.context.locationsIndexed[entry["audit_log.order_activity.sales_location_id"]])
                        ? Location.getDisplayName(this.context.locationsIndexed[entry["audit_log.order_activity.sales_location_id"]])
                        : "";
                    const serviceLocation = Location.getDisplayName(this.context.locationsIndexed[entry["audit_log.order_activity.service_location_id"]])
                        ? Location.getDisplayName(this.context.locationsIndexed[entry["audit_log.order_activity.service_location_id"]])
                        : "";

                    return {
                        "audit_log.row_id": entry["audit_log.row_id"],
                        "audit_log.timestamp": entry["audit_log.timestamp"],
                        "audit_log.userprofile_id": user,
                        "audit_log.order_activity.activity.owner_id": owner,
                        "audit_log.old_value": oldReasonName,
                        "audit_log.new_value": newReasonName,
                        "audit_log.order_activity.order_activity_products._sum_points": entry["audit_log.order_activity.order_activity_products._sum_points"],
                        "audit_log.order_activity.insurance_sub_type_id": insuranceSubTypeName,
                        "audit_log.order_activity.sales_location_id": salesLocation,
                        "audit_log.order_activity.service_location_id": serviceLocation,
                        // 999 Are placeholders for No Reason or any OrderStatusReason that contains a null id
                        "audit_log.new_value_id": entry["audit_log.new_value"] ?? 999,
                        "audit_log.old_value_id": entry["audit_log.old_value"] ?? 999,
                        "audit_log.order_activity.order_type": entry["audit_log.order_activity.order_type"],
                        "audit_log.order_activity.patient_id": entry["audit_log.order_activity.patient_id"],
                        "audit_log.order_activity.marketing_campaign_id": entry["audit_log.order_activity.marketing_campaign_id"],
                    };
                });

                const sankeyRows = orderActivityLogs.map((entry) => {
                    const oldReasonName =
                        OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.old_value"]])
                            ? OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.old_value"]])
                            : "No Reason";
                    const newReasonName =
                        OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.new_value"]])
                            ? OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[entry["audit_log.new_value"]])
                            : "No Reason";

                    return {
                        source: `${oldReasonName}`,
                        // adds an invisble character to the new reason to prevent cycles
                        target: `${newReasonName}` + "\u200b",
                        value: 1
                    }
                });
                this.setState({
                    sankeyRows: sankeyRows,
                    rows: transformedRows,
                    loading: false,
                    loadedAt: new Date(),
                });
            })
            .catch((er) => {
                console.error("Error retrieving order activity logs", er);
                this.setState({ loading: false });
                NotistackEnqueueSnackbar("Error retrieving results", { variant: "error" });
            });
    }

    /**
     * Render the content.
     */
    renderContent() {
        return (
            <>
                <CardReportSettings
                    onRun={(settings) => this.getOrderTimelineAudit(settings)}
                    isLoading={this.state.loading}
                    settingComponents={{
                        dateRange: {
                            validationRules: {
                                beginDate: {
                                    required: true,
                                },
                                endDate: {
                                    required: true,
                                },
                            },
                        },
                        orderStatusReasons: {},
                        userByLocation: {},
                        orderActivityTypes: {},
                        marketingCampaignFilter: {},

                    }}
                />
                <CardReportOrderTimelineAuditChart
                    sankeyRows={this.state.sankeyRows || []}
                    rows={this.state.rows || []}
                    isLoading={this.state.loading}
                    onSankeyClick={(node) => this.handleFilteringData(node)}
                    title={this.getTitle()}
                    loadedAt={this.state.loadedAt}
                    otherCategoryCollapsed={this.state.otherCategoryCollapsed}
                    sankeyChartCollapsed={this.state.sankeyChartCollapsed}
                    getDetailKey={this.getDetailKey()}
                />
                <CardReportOrderTimelineAuditResults
                    rows={this.state.rows || []}
                    columns={this.columns}
                    isLoading={this.state.loading}
                    defaultColumnWidths={this.state.defaultColumnWidths}
                    title={this.getTitle()}
                    grouping={this.state.grouping}
                    sorting={this.state.sorting}
                    filtering={this.state.filtering}
                    onSettingsChange={this.handleFilteringChange.bind(this)}
                    onSortingChange={this.handleSortingChange.bind(this)}
                    onGroupingChange={this.handleGroupingChange.bind(this)}
                    onDelete={(item) => this.handleDelete(item)}
                    columnNames={this.getCSVExportColumnNames()[this.getTitle().toLowerCase()]}
                    loadedAt={this.state.loadedAt}
                    getDetailKey={this.getDetailKey()}
                />
            </>
        );
    }

    /**
     * Render the page header a title
     */
    renderHeader() {
        return (
            <PageHeader title={this.getTitle()} />
        );
    }

    getTitle() {
        return "Order Timeline Audit";
    }

    getMaxWidth() {
        return 1400;
    }
}

